import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useTenants } from '@/features/platform/api';

import { credentialMatchesPlane, type AuthProviderInput, type OidcCredentialViewModel } from '../api';

interface CreateFieldErrors {
  host?: string;
  credentialId?: string;
}

/** First default credential in the (already plane-filtered) list, else the first; '' when none. */
function pickCredential(credentials: readonly OidcCredentialViewModel[], keep: string): string {
  if (credentials.some((c) => c.id === keep)) return keep;
  const preferred = credentials.find((c) => c.isDefault) ?? credentials[0];
  return preferred?.id ?? '';
}

/**
 * Small "identity only" create step (PTV2-1383 / PTV2-1520). The operator picks the PLANE first —
 * Global (default) or a specific tenant — which both sets {@code tenant_uuid} and constrains the OIDC
 * application list to credentials valid for that plane (Global → CLUSTER_OPERATOR only; a tenant →
 * CLIENT / TENANT_OPERATOR), mirroring the backend type↔plane invariant. Everything else is filled with
 * the EMPTY_DRAFT defaults and the provider starts DISABLED; the caller navigates to the detail page to
 * finish configuration (claim mapping, external tenant id, roles, JIT/activate).
 */
export function AuthProviderCreateDialog({
  open,
  credentials,
  onOpenChange,
  onSubmit,
  pending,
  submitError,
}: {
  readonly open: boolean;
  readonly credentials: readonly OidcCredentialViewModel[];
  readonly onOpenChange: (next: boolean) => void;
  readonly onSubmit: (input: AuthProviderInput) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const cp = 'routes.admin.authProviders.create';
  const fp = 'routes.admin.authProviders.form';

  // '' = Global plane (tenant_uuid null); otherwise the selected tenant's UUID.
  const [tenantUuid, setTenantUuid] = useState('');
  const [host, setHost] = useState('');
  const [credentialId, setCredentialId] = useState('');
  const [errors, setErrors] = useState<CreateFieldErrors>({});

  const tenantsQuery = useTenants();
  const tenants = useMemo(() => tenantsQuery.data?.tenants ?? [], [tenantsQuery.data]);

  const isGlobal = tenantUuid.trim().length === 0;
  const visibleCredentials = useMemo(
    () => credentials.filter((c) => credentialMatchesPlane(c.type, isGlobal)),
    [credentials, isGlobal],
  );
  const selectedCredential = visibleCredentials.find((c) => c.id === credentialId);

  useEffect(() => {
    if (open) {
      setTenantUuid('');
      setHost('');
      setCredentialId('');
      setErrors({});
    }
  }, [open]);

  // Changing the plane re-filters the credential list; drop a now-incompatible selection.
  useEffect(() => {
    setCredentialId((current) =>
      visibleCredentials.some((c) => c.id === current) ? current : pickCredential(visibleCredentials, ''),
    );
  }, [visibleCredentials]);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: CreateFieldErrors = {};
    if (host.trim().length === 0) next.host = t(`${fp}.errors.hostRequired`);
    if (credentialId.trim().length === 0) next.credentialId = t(`${fp}.errors.credentialRequired`);
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    // Identity + plane from the modal; everything else at EMPTY_DRAFT defaults. New providers start
    // DISABLED — the operator finishes configuration on the detail page before enabling. The provider
    // inherits its login type from the bound credential (no type is sent). tenant_claim_check defaults on.
    onSubmit({
      host,
      tenantUuid,
      credentialId,
      tenantIdClaimKey: '',
      externalTenantId: '',
      tenantClaimCheckEnabled: true,
      userIdClaimKey: 'sub',
      rolesClaimKey: '',
      rolesFromClaimEnabled: false,
      unmappedRolePolicy: 'FALLBACK_TO_DEFAULTS',
      userinfoEnabled: false,
      claimName: '',
      claimFirstName: '',
      claimLastName: '',
      claimEmail: '',
      jitProvisioningEnabled: false,
      newUserAccountsAreActive: false,
      enabled: false,
      roleMappings: [],
      defaultRoles: [],
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t(`${cp}.title`)}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <p className="text-sm text-muted-foreground">{t(`${cp}.description`)}</p>
          <FormField
            id="create-provider-plane"
            label={t(`${fp}.fields.plane`)}
            hint={t(`${fp}.hints.plane`)}
          >
            {(f) => (
              <Select
                {...f}
                value={tenantUuid}
                onChange={(e) => {
                  setTenantUuid(e.target.value);
                }}
              >
                <option value="">{t(`${fp}.fields.planeGlobal`)}</option>
                {tenants.map((tenant) => (
                  <option key={tenant.uuid} value={tenant.uuid}>
                    {tenant.label.length > 0 ? tenant.label : tenant.name} ({tenant.name})
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          {!isGlobal && tenants.length === 0 && (
            <p className="text-xs text-muted-foreground" role="note">
              {t(`${fp}.hints.noTenants`)}
            </p>
          )}
          <FormField
            id="create-provider-host"
            label={t(`${fp}.fields.host`)}
            hint={t(`${fp}.hints.host`)}
            required
            error={errors.host}
          >
            {(f) => (
              <Input
                {...f}
                value={host}
                onChange={(e) => {
                  setHost(e.target.value);
                }}
                placeholder="nygh.phenotips-staging.com"
                autoComplete="off"
                className="font-mono text-sm"
              />
            )}
          </FormField>
          <FormField
            id="create-provider-credential"
            label={t(`${fp}.fields.credential`)}
            hint={
              selectedCredential !== undefined
                ? t(`${fp}.hints.credentialInheritsType`, {
                    type: t(`routes.admin.oidcCredentials.typeLabel.${selectedCredential.type}`),
                  })
                : t(`${fp}.hints.credentialForPlane`)
            }
            required
            error={errors.credentialId}
          >
            {(f) => (
              <Select
                {...f}
                value={credentialId}
                onChange={(e) => {
                  setCredentialId(e.target.value);
                }}
              >
                <option value="">{t(`${fp}.fields.credentialPlaceholder`)}</option>
                {visibleCredentials.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label} · {t(`routes.admin.oidcCredentials.typeLabel.${c.type}`)}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          {visibleCredentials.length === 0 && (
            <p className="text-xs text-muted-foreground" role="note">
              {t(`${fp}.hints.noCredentialsForPlane`)}
            </p>
          )}

          {submitError !== undefined && submitError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t(`${fp}.errors.submit`, { code: getErrorCodeForUi(submitError) })}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
              }}
              disabled={pending}
            >
              {t(`${cp}.cancel`)}
            </Button>
            <Button type="submit" loading={pending}>
              {t(`${cp}.submit`)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
